import { ReactNode } from 'react';
import { EmptyState as PxEmptyState } from '@roi-pros/px-react';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
}

export function Table<T extends Record<string, unknown>>({
  columns,
  data,
  keyField,
  onRowClick,
  emptyMessage = 'No data available',
}: TableProps<T>) {
  // B5 Platform adoption: the empty state now renders via the Platform Experience
  // canonical empty state (@roi-pros/px-react). The application still owns the decision
  // of WHEN the table is empty (this `data.length === 0` check) and WHAT the message
  // says (the `emptyMessage` prop); the Platform owns HOW that state is rendered
  // (layout, tokens, a11y). The Table API is unchanged — no call site changes.
  if (data.length === 0) {
    return <PxEmptyState model={{ kind: 'empty', title: emptyMessage }} />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.className || ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row) => (
            <tr
              key={String(row[keyField])}
              onClick={() => onRowClick?.(row)}
              className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-4 py-3 text-sm text-gray-900 ${col.className || ''}`}
                >
                  {col.render ? col.render(row) : String(row[col.key as keyof T] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
